/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Shared E-Separation UI components.
 * Built on the project's Bootstrap design system + Tabler (`ti`) icons.
 * Props + status/colour mapping follow the E-Separation master spec.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  ApprovalStep,
  ApproverRole,
  BondSelectionRecord,
  DarModuleCases,
  EmployeeSearchResult,
  EsepDocumentItem,
  formatDate,
  formatINR,
  getDocumentUrl,
  uploadFileToS3,
  LwpLikeRecord,
  OutstandingLoan,
  searchEmployees,
} from "../services/eSeparation.service";

/* Accent palette used by SectionCard top bars + role badges. */
export const ACCENT_PALETTE = [
  "#1a1f36",
  "#185FA5",
  "#0F6E56",
  "#BA7517",
  "#993C1D",
  "#4A1A6E",
  "#1D6E5F",
  "#8B1A1A",
  "#2D5A1E",
  "#5A2D1E",
];

/* ───────────────────────── 1. SectionCard ───────────────────────── */
export const SectionCard: React.FC<{
  index: number;
  title: string;
  subtitle?: string;
  hideIndex?: boolean;
  children?: React.ReactNode;
}> = ({ index, title, subtitle, hideIndex, children }) => {
  const accent = ACCENT_PALETTE[index % ACCENT_PALETTE.length];
  return (
    <div
      className="card border-0 shadow-sm mb-4"
      // overflow stays visible so absolutely-positioned dropdowns (employee
      // search, etc.) are never clipped at the card edge; the accent bar rounds
      // its own top corners instead.
      style={{ borderRadius: 16 }}
    >
      <div style={{ height: 5, background: accent, borderRadius: "16px 16px 0 0" }} />
      <div className="card-body p-4">
        <div className="d-flex align-items-start mb-3">
          {!hideIndex && (
            <span
              className="d-inline-flex align-items-center justify-content-center fw-bold me-3 flex-shrink-0"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: accent,
                color: "#fff",
                fontSize: 14,
              }}
            >
              {String(index + 1).padStart(2, "0")}
            </span>
          )}
          <div>
            <h5 className="mb-0 fw-semibold">{title}</h5>
            {subtitle && <small className="text-muted">{subtitle}</small>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ───────────────────────── 2. FieldLabel ───────────────────────── */
export const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required,
}) => (
  <label
    className="form-label text-uppercase fw-medium text-muted"
    style={{ fontSize: 11, letterSpacing: 0.4 }}
  >
    {label}
    {required && <span className="text-danger ms-1">*</span>}
  </label>
);

/* ──────────────────────── 3. ReadOnlyField ──────────────────────── */
export const ReadOnlyField: React.FC<{ value?: string | number | null }> = ({
  value,
}) => (
  <div
    className="form-control bg-light text-dark"
    style={{ minHeight: 38, cursor: "not-allowed" }}
  >
    {value !== undefined && value !== null && value !== "" ? value : "—"}
  </div>
);

/* ───────────────────────── 4. RadioGroup ────────────────────────── */
export const RadioGroup: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options?: { label: string; value: string }[];
  name?: string;
  disabled?: boolean;
}> = ({ value, onChange, options, name, disabled }) => {
  const opts =
    options || [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ];
  const autoId = React.useId();
  const groupName = name || `rg-${autoId}`;
  return (
    <div className="d-flex gap-4">
      {opts.map((o) => (
        <div className="form-check" key={o.value}>
          <input
            type="radio"
            className="form-check-input"
            name={groupName}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            id={`${groupName}-${o.value}`}
            disabled={disabled}
          />
          <label className="form-check-label" htmlFor={`${groupName}-${o.value}`}>
            {o.label}
          </label>
        </div>
      ))}
    </div>
  );
};

/* ───────────────────────── 5. CheckItem ─────────────────────────── */
export const CheckItem: React.FC<{
  label: string;
  checked: boolean;
  onPress: () => void;
  disabled?: boolean;
}> = ({ label, checked, onPress, disabled }) => {
  const toggle = () => {
    if (!disabled) onPress();
  };
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      className="d-flex align-items-center gap-2 px-3 py-2 rounded h-100 mb-2"
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        border: `1px solid ${checked ? "#185FA5" : "rgba(0,0,0,0.12)"}`,
        background: checked ? "#185FA50F" : "#fff",
        opacity: disabled ? 0.6 : 1,
        transition: "border-color .15s ease, background .15s ease",
        userSelect: "none",
      }}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          toggle();
        }
      }}
    >
      <input
        type="checkbox"
        className="form-check-input mt-0 flex-shrink-0"
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        checked={checked}
        disabled={disabled}
        // Toggle here and stop the event from also firing the row's onClick,
        // which would double-toggle and leave the box visually unchanged.
        onChange={toggle}
        onClick={(e) => e.stopPropagation()}
      />
      <span
        className="small"
        style={{
          color: checked ? "#185FA5" : "#495057",
          fontWeight: checked ? 600 : 400,
        }}
      >
        {label}
      </span>
    </div>
  );
};

/* ───────────────────────── 6. StatusBadge ───────────────────────── */
type BadgeStyle = { color: string; bg: string };

// Raw backend status enums → human-readable labels (badges/labels parity).
const STATUS_LABELS: Record<string, string> = {
  PENDING_APPROVAL: "Pending Approval",
  REACHED_HR: "Reached HR",
  CA_APPROVED: "CA Approved",
  ON_HOLD: "On Hold",
  CLARIFICATION_PENDING: "Clarification Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  WITHDRAWN: "Withdrawn",
  SUPERSEDED: "Superseded",
  CLOSED: "Closed",
  COMPLETED: "Completed",
  CLEARED: "Cleared",
  // Derived status of a No-Dues-only separation case (no approval chain).
  NO_DUES_PENDING: "No-Dues Form Pending",
  NO_DUES_IN_PROGRESS: "No-Dues In Progress",
  NO_DUES_ESTABLISHMENT: "With Establishment",
};

// Show readable text; title-case any other RAW_ENUM_LIKE value while leaving
// already-friendly labels ("In Review", "Approved", …) untouched.
export const formatStatusLabel = (status?: string): string => {
  const raw = String(status ?? "").trim();
  if (!raw) return "—";
  if (STATUS_LABELS[raw.toUpperCase()]) return STATUS_LABELS[raw.toUpperCase()];
  if (/^[A-Z0-9]+(_[A-Z0-9]+)+$/.test(raw)) {
    return raw
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return raw;
};

export const getStatusStyle = (status?: string): BadgeStyle => {
  const s = (status || "").toLowerCase().replace(/_/g, " ");
  if (/(approved|completed|cleared)/.test(s))
    return { color: "#049123", bg: "#E4F7E8" };
  if (/establishment/.test(s)) return { color: "#185FA5", bg: "#E5F0FA" };
  if (/clarification/.test(s)) return { color: "#BA7517", bg: "#FFF1DE" };
  if (/(pending|in progress|reached)/.test(s)) return { color: "#BA7517", bg: "#FFF1DE" };
  if (/(rejected|dues found)/.test(s)) return { color: "#FF0F0F", bg: "#FDE5E5" };
  if (/in review/.test(s)) return { color: "#185FA5", bg: "#E5F0FA" };
  if (/(on hold|hold)/.test(s)) return { color: "#800080", bg: "#F3E5F3" };
  if (/(withdrawn|closed)/.test(s)) return { color: "#78746D", bg: "#EEEDEB" };
  return { color: "#555", bg: "#EEE" };
};

export const StatusBadge: React.FC<{ status?: string }> = ({ status }) => {
  const { color, bg } = getStatusStyle(status);
  return (
    <span
      className="d-inline-flex align-items-center fw-medium"
      style={{
        color,
        background: bg,
        borderRadius: 20,
        padding: "3px 12px",
        fontSize: 12,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: 5,
          background: color,
          display: "inline-block",
          marginRight: 6,
        }}
      />
      {formatStatusLabel(status)}
    </span>
  );
};

/* ───────────────────────── 7. UploadBox ─────────────────────────── */
export const UploadBox: React.FC<{
  onPress?: (files: FileList | null) => void;
  hint?: string;
  files?: { name: string }[];
  accept?: string;
}> = ({ onPress, hint, files, accept }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  return (
    <div>
      <div
        className="border border-2 border-dashed rounded text-center p-4"
        style={{ cursor: "pointer", borderColor: "#cdd5df" }}
        onClick={() => inputRef.current?.click()}
      >
        <i className="ti ti-cloud-upload text-primary fs-2 d-block mb-2" />
        <div className="fw-medium">Choose files to upload</div>
        <small className="text-muted">
          {hint || "PDF, JPG, PNG — max 5 MB each"}
        </small>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="d-none"
          onChange={(e) => {
            onPress?.(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {files && files.length > 0 && (
        <ul className="list-unstyled mt-2 mb-0">
          {files.map((f, i) => (
            <li key={i} className="small text-muted">
              <i className="ti ti-file me-1" />
              {f.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ──────────────────────── 8. BottomActionBar ────────────────────── */
export const BottomActionBar: React.FC<{
  primaryLabel: string;
  primaryIcon?: string;
  secondaryLabel?: string;
  secondaryIcon?: string;
  onPrimary: () => void;
  onSecondary?: () => void;
  primaryColor?: string;
  disabled?: boolean;
  /** Optional helper text shown above the buttons — e.g. why submit is disabled. */
  note?: string;
}> = ({
  primaryLabel,
  primaryIcon,
  secondaryLabel,
  secondaryIcon,
  onPrimary,
  onSecondary,
  primaryColor,
  disabled,
  note,
}) => (
    <div
      className="bg-white border-top py-3 px-2"
      style={{ position: "sticky", bottom: 0, zIndex: 5 }}
    >
      {note && (
        <div
          className="d-flex align-items-start gap-2 mb-2"
          style={{ color: "#C0392B", fontSize: 13 }}
        >
          <i className="ti ti-alert-circle" style={{ marginTop: 1 }} />
          <span>{note}</span>
        </div>
      )}
      <div className="d-flex justify-content-end gap-2">
        {secondaryLabel && (
          <button className="btn btn-outline-secondary px-4" onClick={onSecondary}>
            {secondaryIcon && <i className={`ti ${secondaryIcon} me-1`} />}
            {secondaryLabel}
          </button>
        )}
        <button
          className="btn px-4 text-white"
          style={{ background: primaryColor || "#185FA5" }}
          onClick={onPrimary}
          disabled={disabled}
        >
          {primaryIcon && <i className={`ti ${primaryIcon} me-1`} />}
          {primaryLabel}
        </button>
      </div>
    </div>
  );

/* ─────────────────────── 9. ApprovalTimeline ────────────────────── */
// Date + time, e.g. "20/06/2026, 12:05 AM" (local time).
const formatDateTime = (d?: string): string => {
  if (!d) return "";
  // Already formatted as dd-mm-yyyy — show as-is (no re-parse).
  if (/^\d{2}-\d{2}-\d{4}$/.test(d.trim())) return d.trim();
  const dt = new Date(d);
  // Non-date labels (e.g. "Awaiting action") are shown as-is, like the app.
  if (isNaN(dt.getTime())) return d;
  // Build date + time from the same (local) instant so they stay consistent.
  const day = String(dt.getDate()).padStart(2, "0");
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const year = dt.getFullYear();
  const time = dt.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${day}-${month}-${year}, ${time}`;
};

const TIMELINE_COLORS: Record<string, { color: string; icon: string }> = {
  Approved: { color: "#049123", icon: "ti-check" },
  Pending: { color: "#9aa0a6", icon: "ti-clock" },
  Rejected: { color: "#FF0F0F", icon: "ti-x" },
  Clarification: { color: "#BA7517", icon: "ti-help" },
  Current: { color: "#185FA5", icon: "ti-loader" },
  Skipped: { color: "#94A3B8", icon: "ti-player-skip-forward" },
  Dispatched: { color: "#185FA5", icon: "ti-send" },
  DuesFound: { color: "#FF0F0F", icon: "ti-alert-triangle" },
  Settled: { color: "#BA7517", icon: "ti-cash-banknote" },
  Collected: { color: "#049123", icon: "ti-cash" },
};

export const ApprovalTimeline: React.FC<{
  steps: Partial<ApprovalStep>[];
}> = ({ steps }) => {
  if (!steps || steps.length === 0)
    return <p className="text-muted fst-italic mb-0">No approval actions taken yet.</p>;
  return (
    <div>
      {steps.map((step, i) => {
        const cfg = TIMELINE_COLORS[step.status || "Pending"] || TIMELINE_COLORS.Pending;
        const last = i === steps.length - 1;
        return (
          <div className="d-flex" key={i}>
            <div className="d-flex flex-column align-items-center me-3">
              <span
                className="d-inline-flex align-items-center justify-content-center text-white"
                style={{ width: 28, height: 28, borderRadius: 14, background: cfg.color }}
              >
                <i className={`ti ${cfg.icon}`} style={{ fontSize: 14 }} />
              </span>
              {!last && (
                <span style={{ width: 2, flex: 1, background: "#e3e6ea", minHeight: 24 }} />
              )}
            </div>
            <div className="pb-3">
              <div className="fw-semibold" style={{ color: cfg.color }}>
                {step.level}
              </div>
              {step.approver_name && (
                <div className="small text-muted">{step.approver_name}</div>
              )}
              {step.approver_emp_no && (
                <div className="small text-muted">
                  Emp ID: {step.approver_emp_no}
                </div>
              )}
              {step.action_date && (
                <div className="small text-muted">{formatDateTime(step.action_date)}</div>
              )}
              {step.remarks && <div className="small fst-italic">"{step.remarks}"</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ──────────────────── 10. DepartmentClearanceCard ───────────────── */
export const DepartmentClearanceCard: React.FC<{
  deptName: string;
  nodalOfficer: string;
  status: "Cleared" | "Pending" | "Dues Found";
  remarks?: string;
  dueItems?: string[];
  recoveryAmount?: number;
  // Set when the Nodal Officer has handed this case off to a sub-nodal —
  // shows who is actually processing it right now.
  delegatedTo?: string;
}> = ({ deptName, nodalOfficer, status, remarks, dueItems, recoveryAmount, delegatedTo }) => {
  const map = {
    Cleared: { color: "#049123", icon: "ti-circle-check" },
    Pending: { color: "#BA7517", icon: "ti-clock" },
    "Dues Found": { color: "#FF0F0F", icon: "ti-alert-triangle" },
  } as const;
  const cfg = map[status] || map.Pending;
  return (
    <div
      className="card border-0 shadow-sm mb-3"
      style={{ borderLeft: `4px solid ${cfg.color}`, borderRadius: 10 }}
    >
      <div className="card-body py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <i className={`ti ${cfg.icon}`} style={{ color: cfg.color, fontSize: 18 }} />
            <div>
              <div className="fw-semibold">{deptName}</div>
              <small className="text-muted">Nodal: {nodalOfficer || "—"}</small>
              {delegatedTo && (
                <small className="d-block" style={{ color: "#993C1D" }}>
                  <i className="ti ti-arrow-forward-up me-1" />
                  Delegated to: {delegatedTo}
                </small>
              )}
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
        {remarks && <div className="small mt-2 text-muted">Remarks: {remarks}</div>}
        {dueItems && dueItems.length > 0 && (
          <ul className="small mt-2 mb-0">
            {dueItems.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        )}
        {recoveryAmount !== undefined && recoveryAmount > 0 && (
          <div className="small mt-1 fw-medium text-danger">
            Recovery Amount: {formatINR(recoveryAmount)}
          </div>
        )}
      </div>
    </div>
  );
};

/* ───────────────────────── 11. WarningBanner ────────────────────── */
export const WarningBanner: React.FC<{
  message: React.ReactNode;
  type?: "warning" | "info" | "error" | "success";
}> = ({ message, type = "warning" }) => {
  const map = {
    warning: { bg: "#FFF1DE", color: "#8a5a00", icon: "ti-alert-triangle" },
    info: { bg: "#E5F0FA", color: "#185FA5", icon: "ti-info-circle" },
    error: { bg: "#FDE5E5", color: "#b21f1f", icon: "ti-circle-x" },
    success: { bg: "#E4F7E8", color: "#1d7a33", icon: "ti-circle-check" },
  };
  const cfg = map[type];
  return (
    <div
      className="d-flex align-items-start gap-2 rounded p-3 mb-3"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <i className={`ti ${cfg.icon} mt-1`} />
      <div className="small">{message}</div>
    </div>
  );
};

/* ───────────────────────────── 12. InfoRow ──────────────────────── */
export const InfoRow: React.FC<{
  label: string;
  value?: React.ReactNode;
  valueColor?: string;
  bold?: boolean;
}> = ({ label, value, valueColor, bold }) => (
  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
    <span className="text-muted small">{label}</span>
    <span
      className={bold ? "fw-bold" : "fw-medium"}
      style={{ color: valueColor, textAlign: "right" }}
    >
      {value !== undefined && value !== null && value !== "" ? value : "—"}
    </span>
  </div>
);

/* ─────────────────────── 13. ActionButtonGroup ──────────────────── */
export const ActionButtonGroup: React.FC<{
  onApprove: () => void;
  onReject: () => void;
  onSendBack?: () => void;
  onClarification?: () => void;
  loading?: boolean;
  approveLabel?: string;
  rejectLabel?: string;
}> = ({
  onApprove,
  onReject,
  onSendBack,
  onClarification,
  loading,
  approveLabel,
  rejectLabel,
}) => (
    <div>
      <div className="row g-2">
        <div className="col-6">
          <button
            className="btn w-100 text-white"
            style={{ background: "#049123" }}
            onClick={onApprove}
            disabled={loading}
          >
            <i className="ti ti-check me-1" />
            {approveLabel || "Approve"}
          </button>
        </div>
        <div className="col-6">
          <button
            className="btn w-100 text-white"
            style={{ background: "#C0282C" }}
            onClick={onReject}
            disabled={loading}
          >
            <i className="ti ti-x me-1" />
            {rejectLabel || "Reject"}
          </button>
        </div>
      </div>
      {onClarification && (
        <div className="row g-2 mt-1">
          {/* Send Back is disabled for now (matches the mobile app). */}
          {false && onSendBack && (
            <div className="col-6">
              <button
                className="btn w-100 text-white"
                style={{ background: "#BA7517" }}
                onClick={onSendBack}
                disabled={loading}
              >
                <i className="ti ti-arrow-back-up me-1" />
                Send Back
              </button>
            </div>
          )}
          {onClarification && (
            <div className="col-6">
              <button
                className="btn w-100 text-white"
                style={{ background: "#185FA5" }}
                onClick={onClarification}
                disabled={loading}
              >
                <i className="ti ti-help me-1" />
                Clarification
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

/* ─────────────────────── 14. ApproverRoleBadge ──────────────────── */
const ROLE_STYLE: Record<string, { color: string; icon: string }> = {
  Deputy: { color: "#185FA5", icon: "ti-user" },
  CO: { color: "#185FA5", icon: "ti-user-check" },
  HOD: { color: "#4A1A6E", icon: "ti-users" },
  Vigilance: { color: "#4A1A6E", icon: "ti-shield" },
  HR: { color: "#BA7517", icon: "ti-briefcase" },
  Nodal: { color: "#BA7517", icon: "ti-building" },
  CA: { color: "#049123", icon: "ti-gavel" },
  Finance: { color: "#049123", icon: "ti-cash" },
  MD: { color: "#993C1D", icon: "ti-crown" },
};

export const ApproverRoleBadge: React.FC<{ role: ApproverRole | string; label?: string }> = ({
  role,
  label,
}) => {
  const cfg = ROLE_STYLE[role] || { color: "#555", icon: "ti-user" };
  return (
    <span
      className="d-inline-flex align-items-center fw-medium"
      style={{
        color: cfg.color,
        background: `${cfg.color}1a`,
        borderRadius: 20,
        padding: "4px 12px",
        fontSize: 12,
      }}
    >
      <i className={`ti ${cfg.icon} me-1`} />
      {label || role}
    </span>
  );
};

/* ─────────────────────── 15. CollapsibleSection ─────────────────── */
export const CollapsibleSection: React.FC<{
  title: string;
  icon?: string;
  iconColor?: string;
  iconBg?: string;
  defaultExpanded?: boolean;
  badge?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, icon, iconColor, iconBg, defaultExpanded, badge, children }) => {
  const [open, setOpen] = useState(!!defaultExpanded);
  return (
    <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
      <div
        className="card-body py-3 d-flex align-items-center justify-content-between"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="d-flex align-items-center gap-2">
          {icon && (
            <span
              className="d-inline-flex align-items-center justify-content-center"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: iconBg || "#eef2f7",
                color: iconColor || "#185FA5",
              }}
            >
              <i className={`ti ${icon}`} />
            </span>
          )}
          <span className="fw-semibold">{title}</span>
          {badge}
        </div>
        <i className={`ti ti-chevron-${open ? "up" : "down"} text-muted`} />
      </div>
      {open && <div className="card-body pt-0">{children}</div>}
    </div>
  );
};

/* ─────────────── 15b. EmployeeSearchInput (type → search → select) ─────────────── */
// Extracted from the identical hand-rolled pattern in HrInitiateCase.tsx /
// NoDuesDepartmentConfig.tsx — a debounced "type employee ID or name, see
// matches, select one" control. Fully controlled: the caller owns `value`
// (what's shown in the box) and receives the picked employee via `onSelect`.
export const EmployeeSearchInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  onSelect: (item: EmployeeSearchResult) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ value, onChange, onSelect, placeholder, disabled }) => {
  const [results, setResults] = useState<EmployeeSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a stale search response overwriting a newer one.
  const searchSeq = useRef(0);

  // Only searches in response to the user actually typing (handleChange
  // below) — never on mount or when the parent re-seeds `value` with an
  // already-known employee ID (e.g. from a prefilled record), so a
  // pre-selected employee never shows as an unconfirmed suggestion the user
  // has to click again.
  const search = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (query.length < 2) {
      setResults([]);
      setSearching(false);
      setShowDropdown(false);
      return;
    }
    setSearching(true);
    const seq = ++searchSeq.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await searchEmployees(query);
        if (seq === searchSeq.current) {
          setResults(list);
          setShowDropdown(true);
        }
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 350);
  };

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const handleChange = (v: string) => {
    onChange(v);
    search(v);
  };

  return (
    // Each instance's z-index must jump above its siblings ONLY while its own
    // dropdown is open — a fixed z-index here would just set up a tie between
    // every instance on the page, and when multiple EmployeeSearchInputs are
    // stacked (e.g. a sureties table), the one later in the DOM always wins
    // that tie and paints over an EARLIER row's still-open dropdown.
    <div className="position-relative" style={{ zIndex: showDropdown ? 1000 : 1 }}>
      <div className="input-group">
        <span className="input-group-text bg-white">
          <i className="ti ti-search text-muted" />
        </span>
        <input
          className="form-control"
          placeholder={placeholder || "Type employee number or name…"}
          value={value}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => results.length > 0 && setShowDropdown(true)}
        />
        {searching ? (
          <span className="input-group-text bg-white">
            <span className="spinner-border spinner-border-sm text-secondary" role="status" />
          </span>
        ) : value.length > 0 ? (
          <button
            className="input-group-text bg-white border-start-0"
            type="button"
            onClick={() => {
              onChange("");
              setResults([]);
              setShowDropdown(false);
            }}
          >
            <i className="ti ti-circle-x text-muted" />
          </button>
        ) : null}
      </div>

      {showDropdown && results.length > 0 && (
        <div
          className="card border shadow-sm position-absolute w-100 mt-1"
          style={{ borderRadius: 12, overflow: "hidden", zIndex: 20 }}
        >
          {results.map((item) => (
            <button
              key={item.emp_no}
              type="button"
              className="btn text-start d-flex align-items-center gap-2 px-3 py-2 border-bottom rounded-0"
              onClick={() => {
                setShowDropdown(false);
                setResults([]);
                onSelect(item);
              }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 30, height: 30, borderRadius: 15, background: "rgba(0,0,0,0.04)", color: "#185FA5" }}
              >
                <i className="ti ti-user" />
              </span>
              <span className="flex-grow-1">
                <span className="d-block fw-medium">{item.emp_name || "—"}</span>
                <small className="text-muted">
                  {item.emp_no}
                  {item.designation ? `  ·  ${item.designation}` : ""}
                </small>
              </span>
              <i className="ti ti-chevron-right text-muted" />
            </button>
          ))}
        </div>
      )}
      {showDropdown && !searching && value.trim().length >= 2 && results.length === 0 && (
        <div
          className="card border shadow-sm position-absolute w-100 mt-1 text-center text-muted small py-3"
          style={{ borderRadius: 12, zIndex: 20 }}
        >
          No matching employees found.
        </div>
      )}
    </div>
  );
};

/* ─────────────── 15c. LeaveRecordsDisplay (CCL / LWP, auto-fetched) ─────────────── */
// "Child Care Leave (CCL) taken in last 2 years?" / "Leave Without Pay (LWP)
// taken in last 2 years?" — always auto-fetched from the SAP-synced
// sap_lwp_records table, never employee- or HR-editable (matches the
// employee form's own "cannot be edited" framing for every other
// auto-fetched-from-SAP field).
export const LeaveRecordsDisplay: React.FC<{
  records: LwpLikeRecord[];
  loading?: boolean;
}> = ({ records, loading }) => (
  <div>
    {loading ? (
      <p className="text-muted fst-italic mb-0">Loading…</p>
    ) : records.length === 0 ? (
      <span className="badge bg-light text-dark border">No</span>
    ) : (
      <>
        <span className="badge bg-success-transparent text-success mb-2">Yes</span>
        <div className="d-flex flex-column gap-1">
          {records.map((r, i) => (
            <div key={i} className="small text-dark">
              {formatDate(r.start_date)} – {formatDate(r.end_date)} ({r.days} day{r.days === 1 ? "" : "s"})
            </div>
          ))}
        </div>
      </>
    )}
    <small className="text-secondary fw-medium d-block mt-1">
      Auto-fetched from SAP leave records — cannot be edited.
    </small>
  </div>
);

/* ─────────────── 16. OutstandingLoansSection (auto-captured) ─────────────── */
export const OutstandingLoansSection: React.FC<{
  loans?: OutstandingLoan[];
  loading?: boolean;
  note?: string;
  // When provided, renders an editable "Declared Amount" input next to each
  // SAP loan (keyed by reference_number) instead of a plain read-only row —
  // the correction is recorded in e-separation only, never written back to
  // sap_loan_requests.
  overrides?: Record<string, string>;
  onOverrideChange?: (referenceNumber: string, value: string) => void;
}> = ({ loans, loading, note, overrides, onOverrideChange }) => {
  const list = loans || [];
  const total = list.reduce((s, l) => s + (Number(l.balance) || 0), 0);
  const editable = !!onOverrideChange;
  return (
    <div>
      <WarningBanner
        type="info"
        message={
          note ||
          (editable
            ? "Your outstanding loans are auto-fetched from your SAP loan records. You may declare a corrected amount below"
            : "Your outstanding loans are auto-fetched from your SAP loan records and cannot be edited here.")
        }
      />
      {loading ? (
        <p className="text-muted fst-italic mb-0 text-center py-2">
          Loading outstanding loans…
        </p>
      ) : list.length > 0 ? (
        <>
          {list.map((l, i) => {
            const key = l.reference_number || l.loan_code || String(i);
            return (
              <div
                key={key + i}
                className="d-flex justify-content-between align-items-center p-2 rounded mb-2 flex-wrap gap-2"
                style={{ background: "rgba(0,0,0,0.03)" }}
              >
                <div className="flex-grow-1 me-2">
                  <div className="fw-semibold">
                    {l.loan_name || l.loan_code || "Loan"}
                  </div>
                  {l.reference_number && (
                    <small className="text-muted">Ref: {l.reference_number}</small>
                  )}
                </div>
                <div className="text-end">
                  <div className="fw-semibold" style={{ color: "#C0282C" }}>
                    {formatINR(l.balance)}
                  </div>
                  <small className="text-muted">Balance</small>
                </div>
                {editable && l.reference_number && (
                  <div style={{ width: 160 }}>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder="Declared amount"
                      value={overrides?.[l.reference_number] ?? ""}
                      onChange={(e) => onOverrideChange(l.reference_number as string, e.target.value)}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div className="d-flex justify-content-between align-items-center border-top pt-2 mt-1">
            <span className="fw-semibold">Total Outstanding</span>
            <span className="fw-bold" style={{ color: "#C0282C" }}>
              {formatINR(total)}
            </span>
          </div>
        </>
      ) : (
        <p className="text-muted fst-italic mb-0 text-center py-2">
          No outstanding loans found.
        </p>
      )}
    </div>
  );
};

/* ─── D&AR Case History (real module records) — approver/HR view only ─── */
const darCaseRow = (key: string, title: string, meta: string, status: string, detail?: string | null) => (
  <div
    key={key}
    className="p-2 rounded mb-2"
    style={{ background: "rgba(0,0,0,0.03)" }}
  >
    <div className="d-flex justify-content-between align-items-start">
      <div className="flex-grow-1 me-2">
        <div className="fw-semibold">{title}</div>
        <small className="text-muted">{meta}</small>
      </div>
      <StatusBadge status={status} />
    </div>
    {detail && <div className="small mt-1">{detail}</div>}
  </div>
);

export const DarCaseHistorySection: React.FC<{
  cases?: DarModuleCases | null;
}> = ({ cases }) => {
  const contemplated = cases?.contemplated ?? [];
  const pending = cases?.pending ?? [];
  const suspensions = cases?.suspensions ?? [];
  const closed = cases?.closed ?? [];
  const total = contemplated.length + pending.length + suspensions.length + closed.length;

  if (total === 0) {
    return (
      <WarningBanner type="success" message="No D&AR case history found for this employee." />
    );
  }

  return (
    <div>
      {pending.length > 0 && (
        <>
          <div className="fw-semibold small text-muted mb-2">Pending Cases</div>
          {pending.map((p) =>
            darCaseRow(
              `pending-${p.id}`,
              `Case ${p.caseno}`,
              [p.charge_type, p.chargesheetdate ? `Chargesheet: ${formatDate(p.chargesheetdate)}` : null]
                .filter(Boolean)
                .join(" • "),
              p.status,
              p.remarks
            )
          )}
        </>
      )}
      {contemplated.length > 0 && (
        <>
          <div className="fw-semibold small text-muted mb-2 mt-3">Contemplated Cases</div>
          {contemplated.map((c) =>
            darCaseRow(
              `contemplated-${c.caseno}`,
              `Case ${c.caseno}`,
              [c.casetype, c.createddate ? `Since: ${formatDate(c.createddate)}` : null]
                .filter(Boolean)
                .join(" • "),
              c.status,
              c.casedescription
            )
          )}
        </>
      )}
      {suspensions.length > 0 && (
        <>
          <div className="fw-semibold small text-muted mb-2 mt-3">Suspension Cases</div>
          {suspensions.map((s) =>
            darCaseRow(
              `suspension-${s.id}`,
              `Case ${s.caseno}`,
              [
                s.susp_type,
                `From ${formatDate(s.susp_start_date)}`,
                s.susp_end_date ? `to ${formatDate(s.susp_end_date)}` : "ongoing",
              ]
                .filter(Boolean)
                .join(" • "),
              s.status
            )
          )}
        </>
      )}
      {closed.length > 0 && (
        <>
          <div className="fw-semibold small text-muted mb-2 mt-3">
            Closed Cases (Guilty Found)
          </div>
          {closed.map((c) =>
            darCaseRow(
              `closed-${c.id}`,
              `Case ${c.caseno}`,
              [
                c.penalty_type,
                c.penalty_start_date ? `From ${formatDate(c.penalty_start_date)}` : null,
                c.penalty_end_date ? `to ${formatDate(c.penalty_end_date)}` : null,
              ]
                .filter(Boolean)
                .join(" • "),
              c.status,
              c.penalty_desc
            )
          )}
        </>
      )}
    </div>
  );
};

/* ─────────── 17. ClarificationThread (approver Q / employee reply) ─────────── */
export interface ClarificationRow {
  id: number;
  query_string?: string | null;
  response?: string | null;
  resolved?: boolean;
  raised_by?: string | null;
  raised_by_name?: string | null;
  // Additional documents the employee attached to the reply.
  documents?: Array<{ id: number; doc_name?: string | null; file_name?: string; s3_key: string }>;
}

type ReplyAttachment = {
  key: number;
  name: string;
  file: { s3_key: string; file_name: string } | null;
  uploading: boolean;
};

export const ClarificationThread: React.FC<{
  clarifications?: ClarificationRow[];
  // Provided only for the employee → shows a reply box for the open question.
  onReply?: (
    clarificationId: number,
    text: string,
    documents?: { doc_name: string; s3_key: string; file_name: string }[]
  ) => void;
  replying?: boolean;
}> = ({ clarifications, onReply, replying }) => {
  const [reply, setReply] = useState("");
  // Additional documents the employee attaches to the reply (each with a name).
  const attKey = useRef(0);
  const [attachments, setAttachments] = useState<ReplyAttachment[]>([]);
  const patchAtt = (key: number, patch: Partial<ReplyAttachment>) =>
    setAttachments((a) => a.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  const addAtt = () =>
    setAttachments((a) => [...a, { key: attKey.current++, name: "", file: null, uploading: false }]);
  const uploadAtt = async (key: number, fl: FileList | null) => {
    const file = fl?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      return toast.warning("Only JPG, PNG and PDF files are supported.");
    }
    if (file.size / (1024 * 1024) > 5) return toast.warning("File is larger than 5 MB.");
    patchAtt(key, { uploading: true });
    try {
      const s3 = await uploadFileToS3(file);
      if (!s3) throw new Error("Upload failed");
      patchAtt(key, { file: { s3_key: s3.key, file_name: file.name } });
    } catch (e: any) {
      toast.error(e?.message || "Failed to upload the document.");
    } finally {
      patchAtt(key, { uploading: false });
    }
  };
  const openDoc = async (s3_key: string) => {
    const url = await getDocumentUrl(s3_key);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else toast.error("Could not open this document. Please try again.");
  };
  const list = clarifications || [];
  if (list.length === 0) {
    return (
      <p className="text-muted fst-italic mb-0 text-center py-2">
        No clarifications have been raised.
      </p>
    );
  }
  // The most recent question still awaiting an answer.
  const open = [...list].reverse().find((c) => !c.response && !c.resolved);
  return (
    <div>
      {list.map((c) => (
        <div key={c.id} className="mb-3">
          <div
            className="p-2 rounded"
            style={{
              background: "#EAF2FB",
              borderTopLeftRadius: 2,
              maxWidth: "92%",
            }}
          >
            <div className="d-flex align-items-center gap-1 mb-1">
              <i
                className="ti ti-help-circle"
                style={{ color: "#185FA5", fontSize: 13 }}
              />
              <small className="text-muted">
                {(c.raised_by_name || c.raised_by || "Approver") + " asked"}
              </small>
            </div>
            <div className="small">{c.query_string || "—"}</div>
          </div>

          {c.response && (
            <div
              className="p-2 rounded ms-auto mt-2"
              style={{
                background: "#E1F5EE",
                borderTopRightRadius: 2,
                maxWidth: "92%",
              }}
            >
              <div className="d-flex align-items-center gap-1 mb-1">
                <i
                  className="ti ti-message-circle"
                  style={{ color: "#0F6E56", fontSize: 13 }}
                />
                <small className="text-muted">Employee replied</small>
              </div>
              <div className="small">{c.response}</div>
              {!!c.documents?.length && (
                <div className="mt-2 pt-2 border-top d-flex flex-column gap-1">
                  <small className="text-muted">Additional documents</small>
                  {c.documents.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className="btn btn-sm btn-link text-start p-0 d-flex align-items-center gap-1"
                      onClick={() => openDoc(d.s3_key)}
                    >
                      <i className="ti ti-paperclip" />
                      <span>{d.doc_name || d.file_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!c.response && !onReply && (
            <div className="small fst-italic text-muted mt-1">
              Awaiting employee response…
            </div>
          )}
        </div>
      ))}

      {onReply && open && (
        <div className="border-top pt-3 mt-2">
          <FieldLabel label="Your Reply" />
          <textarea
            className="form-control mb-2"
            rows={3}
            placeholder="Type your reply to the approver…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <FieldLabel label="Additional Documents (if asked for)" />
          {attachments.map((a) => (
            <div className="row g-2 align-items-center mb-2" key={a.key}>
              <div className="col-md-5">
                <input
                  className="form-control form-control-sm"
                  placeholder="Document name"
                  value={a.name}
                  onChange={(e) => patchAtt(a.key, { name: e.target.value })}
                />
              </div>
              <div className="col-md-7 d-flex align-items-center gap-2 flex-wrap">
                <input
                  type="file"
                  id={`clarDocInput-${a.key}`}
                  className="d-none"
                  accept=".pdf,.jpg,.jpeg,.png"
                  disabled={a.uploading}
                  onChange={(e) => {
                    uploadAtt(a.key, e.target.files);
                    e.target.value = "";
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={a.uploading}
                  onClick={() => document.getElementById(`clarDocInput-${a.key}`)?.click()}
                >
                  <i className="ti ti-upload me-1" />
                  {a.uploading ? "Uploading..." : a.file ? "Replace" : "Upload Document"}
                </button>
                {a.file && !a.uploading && (
                  <span className="small text-success text-truncate" style={{ maxWidth: 220 }} title={a.file.file_name}>
                    <i className="ti ti-file-check me-1" />
                    {a.file.file_name}
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger ms-auto"
                  onClick={() => setAttachments((x) => x.filter((y) => y.key !== a.key))}
                  title="Remove"
                >
                  <i className="ti ti-trash" />
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={addAtt}>
            <i className="ti ti-plus me-1" />
            Add Document
          </button>
          <div>
          <button
            className="btn text-white"
            style={{ background: "#185FA5" }}
            disabled={!reply.trim() || replying || attachments.some((a) => a.uploading)}
            onClick={() => {
              if (attachments.some((a) => !a.file || !a.name.trim())) {
                return toast.warning("Give every added document a name and upload its file, or remove it.");
              }
              onReply(
                open.id,
                reply.trim(),
                attachments.map((a) => ({
                  doc_name: a.name.trim(),
                  s3_key: a.file!.s3_key,
                  file_name: a.file!.file_name,
                }))
              );
              setReply("");
              setAttachments([]);
            }}
          >
            <i className="ti ti-send me-1" />
            {replying ? "Sending…" : "Send Reply"}
          </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── 18. DocumentList (files attached during resignation — visible to the ───
   employee, every approver in the chain, and HR, since they all load the same
   request detail). Each row resolves a fresh presigned URL on demand. ─── */

const DOC_TYPE_LABELS: Record<string, string> = {
  NOC: "NOC",
  QUALIFICATION: "Qualification Certificate",
  BOND_TRANSFER: "Bond Transfer",
  GRATUITY_TRANSFER: "Gratuity Transfer",
  SN_ATTACHMENT: "Short Notice Attachment",
  CLARIFICATION: "Additional Document",
};

const fileIconFor = (fileName?: string): string => {
  const ext = (fileName || "").split(".").pop()?.toLowerCase() || "";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "ti-photo";
  if (ext === "pdf") return "ti-file-type-pdf";
  if (["doc", "docx"].includes(ext)) return "ti-file-type-doc";
  return "ti-file";
};

export const DocumentList: React.FC<{
  documents?: EsepDocumentItem[];
  emptyText?: string;
}> = ({ documents, emptyText }) => {
  const [openingId, setOpeningId] = useState<number | null>(null);
  const list = documents || [];

  const handleView = async (doc: EsepDocumentItem) => {
    setOpeningId(doc.id);
    const url = await getDocumentUrl(doc.s3_key);
    setOpeningId(null);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast.error("Could not open this document. Please try again.");
    }
  };

  if (list.length === 0) {
    return (
      <p className="text-muted fst-italic mb-0 text-center py-2">
        {emptyText || "No documents uploaded for this request."}
      </p>
    );
  }

  const renderRow = (doc: EsepDocumentItem) => (
    <div
      key={doc.id}
      className="d-flex align-items-center gap-3 p-2 rounded"
      style={{ background: "rgba(0,0,0,0.03)" }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 38, height: 38, borderRadius: 10, background: "#E6F1FB", color: "#185FA5" }}
      >
        <i className={`ti ${fileIconFor(doc.file_name)}`} style={{ fontSize: 18 }} />
      </span>
      <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
        <div className="fw-semibold text-truncate">{doc.doc_name || doc.file_name || "Document"}</div>
        <small className="text-muted">
          {doc.doc_name && doc.file_name && doc.doc_name !== doc.file_name ? `${doc.file_name} • ` : ""}
          {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
          {doc.created_at ? ` • Uploaded ${formatDate(doc.created_at)}` : ""}
        </small>
      </div>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary flex-shrink-0"
        onClick={() => handleView(doc)}
        disabled={openingId === doc.id}
      >
        {openingId === doc.id ? (
          <span className="spinner-border spinner-border-sm" />
        ) : (
          <>
            <i className="ti ti-eye me-1" />
            View
          </>
        )}
      </button>
    </div>
  );

  // Documents the employee attached while answering a clarification are listed
  // apart, as "Additional Documents".
  const main = list.filter((d) => d.doc_type !== "CLARIFICATION");
  const additional = list.filter((d) => d.doc_type === "CLARIFICATION");

  return (
    <div className="d-flex flex-column gap-2">
      {main.map(renderRow)}
      {additional.length > 0 && (
        <>
          <div className="text-uppercase text-muted fw-semibold mt-2" style={{ fontSize: 11, letterSpacing: 1.1 }}>
            Additional Documents (submitted in reply to a clarification)
          </div>
          {additional.map(renderRow)}
        </>
      )}
    </div>
  );
};

/* Formats one bond-selection field value per its snapshotted field_type. */
const formatBondFieldValue = (value: any, fieldType?: string): React.ReactNode => {
  if (value === undefined || value === null || value === "") return "—";
  if (fieldType === "BOOLEAN") return value ? "Yes" : "No";
  if (fieldType === "NUMBER") return formatINR(Number(value) || 0);
  return String(value);
};

// Renders the bond types an employee selected during resignation + the values
// they filled in for each type's dynamic fields (see EsepRequestBondSelection
// on the backend — field_meta/values are a snapshot taken at submission time,
// so this always renders correctly even if HR later edits the bond type).
export const BondSelectionsSection: React.FC<{
  selections?: BondSelectionRecord[];
  emptyText?: string;
}> = ({ selections, emptyText }) => {
  const list = selections || [];

  if (list.length === 0) {
    return (
      <p className="text-muted fst-italic mb-0 text-center py-2">
        {emptyText || "No service bonds selected for this request."}
      </p>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {list.map((sel) => (
        <div
          key={sel.id}
          className="p-2 rounded"
          style={{ background: "rgba(0,0,0,0.03)" }}
        >
          <div className="fw-semibold mb-1">{sel.bond_type_name}</div>
          {(sel.field_meta || []).map((f) => (
            <InfoRow
              key={f.field_key}
              label={f.field_label}
              value={formatBondFieldValue(sel.values?.[f.field_key], f.field_type)}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

/* ───────────────────── Shared layout helpers ────────────────────── */
export const PageHeader: React.FC<{
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
  onExit?: () => void;
}> = ({ title, subtitle, right, onExit }) => (
  <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
    <div className="my-auto mb-2">
      <h3 className="page-title mb-1">{title}</h3>
      {subtitle && <p className="text-muted mb-0">{subtitle}</p>}
    </div>
    <div className="d-flex align-items-center gap-2">
      {right}
      {onExit && (
        <button className="btn btn-outline-secondary" onClick={onExit}>
          <i className="ti ti-logout me-1" />
          Exit
        </button>
      )}
    </div>
  </div>
);

export const EmptyState: React.FC<{
  icon?: string;
  title?: string;
  message?: string;
  action?: React.ReactNode;
}> = ({ icon, title, message, action }) => (
  <div className="text-center py-5">
    <i className={`ti ${icon || "ti-inbox"} text-muted`} style={{ fontSize: 48 }} />
    {title && <h5 className="mt-3 mb-1">{title}</h5>}
    {message && <p className="text-muted mt-3">{message}</p>}
    {action}
  </div>
);


export const DeptClearanceDetailModal: React.FC<{
  open: boolean;
  deptName: string;
  loading?: boolean;
  items: { item_name: string; comments?: string | null; amount_due: number }[];
  onClose: () => void;
}> = ({ open, deptName, loading, items, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      style={{ background: "rgba(0,0,0,0.5)", zIndex: 1080 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0" style={{ borderRadius: 14 }}>
          <div className="modal-header border-0 pb-2">
            <h5 className="modal-title fw-semibold">
              {deptName || "Department Details"}
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            />
          </div>
          <div className="modal-body pt-0">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-secondary" role="status" />
                <p className="text-muted mt-2 mb-0">Loading items...</p>
              </div>
            ) : items.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {items.map((item, idx) => (
                  <div
                    key={idx}
                    className="d-flex justify-content-between align-items-start p-2 rounded"
                    style={{ background: "rgba(0,0,0,0.03)" }}
                  >
                    <div className="flex-grow-1 me-2">
                      <div className="fw-semibold">{item.item_name}</div>
                      {item.comments ? (
                        <small className="text-muted">{item.comments}</small>
                      ) : null}
                    </div>
                    <span className="fw-semibold" style={{ color: "#C0282C" }}>
                      {formatINR(item.amount_due)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <i
                  className="ti ti-clipboard text-muted"
                  style={{ fontSize: 36 }}
                />
                <p className="text-muted mt-2 mb-0">
                  No items recorded for this department
                </p>
              </div>
            )}
          </div>
          <div className="modal-footer border-0 pt-0">
            <button
              type="button"
              className="btn text-white px-4"
              style={{ background: "#993C1D" }}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────────── ConfirmModal ─────────────────────────── */
export type ConfirmTone = "primary" | "danger" | "success" | "warning";

const CONFIRM_TONE: Record<ConfirmTone, { btn: string; icon: string; color: string; bg: string }> = {
  primary: { btn: "btn-primary", icon: "ti-help-circle", color: "#185FA5", bg: "#E6F1FB" },
  danger: { btn: "btn-danger", icon: "ti-alert-triangle", color: "#C0282C", bg: "#FDE5E5" },
  success: { btn: "btn-success", icon: "ti-circle-check", color: "#049123", bg: "#E4F7E8" },
  warning: { btn: "btn-warning", icon: "ti-alert-triangle", color: "#BA7517", bg: "#FFF1DE" },
};

export const ConfirmModal: React.FC<{
  open: boolean;
  title?: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({
  open,
  title = "Confirm Action",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  onConfirm,
  onCancel,
}) => {
    if (!open) return null;
    const cfg = CONFIRM_TONE[tone];
    return (
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        style={{ background: "rgba(0,0,0,0.5)", zIndex: 1080 }}
        onClick={onCancel}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content border-0" style={{ borderRadius: 14 }}>
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title fw-semibold">{title}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} />
            </div>
            <div className="modal-body">
              <div className="d-flex align-items-start gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 42, height: 42, borderRadius: 12, background: cfg.bg, color: cfg.color }}
                >
                  <i className={`ti ${cfg.icon}`} style={{ fontSize: 22 }} />
                </span>
                <div className="pt-1">{message}</div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button type="button" className="btn btn-outline-secondary px-4" onClick={onCancel}>
                {cancelLabel}
              </button>
              <button type="button" className={`btn ${cfg.btn} px-4`} onClick={onConfirm}>
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

/**
 * Promise-based confirmation modal hook (drop-in replacement for window.confirm).
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   ...
 *   if (!(await confirm("Are you sure?"))) return;
 *   ...
 *   return (<div>{confirmDialog}{...rest}</div>);
 */
export const useConfirm = () => {
  const [state, setState] = useState<{
    open: boolean;
    message: React.ReactNode;
    title?: string;
    confirmLabel?: string;
    tone?: ConfirmTone;
    resolver?: (value: boolean) => void;
  }>({ open: false, message: "" });

  const confirm = useCallback(
    (
      message: React.ReactNode,
      opts?: { title?: string; confirmLabel?: string; tone?: ConfirmTone }
    ): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        setState({
          open: true,
          message,
          title: opts?.title,
          confirmLabel: opts?.confirmLabel,
          tone: opts?.tone,
          resolver: resolve,
        });
      }),
    []
  );

  const settle = (value: boolean) => {
    state.resolver?.(value);
    setState((s) => ({ ...s, open: false, resolver: undefined }));
  };

  const confirmDialog = (
    <ConfirmModal
      open={state.open}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      tone={state.tone}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, confirmDialog };
};
